import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession, signIn, confirmSignIn, signOut } from 'aws-amplify/auth';
import { setReauthInProgress } from './reauth-state';

/**
 * Re-auth provider (ADR-063) — the single, shared detection + MFA step-up
 * re-authentication path for the staff portal.
 *
 * Session model (NIST 800-63B AAL2), supersedes ADR-009's flat ≤60-min rule:
 *   - Absolute re-auth ceiling: 24h since last full authentication
 *     (server-authoritative in admin-api; mirrored here for proactive detect).
 *   - Inactivity timeout: 1h of no user interaction (tracked SPA-side here).
 *   - Re-auth factor: MFA (TOTP), re-run in place — no full logout, no reload.
 *
 * Enforcement: a state-mutating admin action must call requireFreshAuth()
 * (directly, or via withReauth()) BEFORE it dispatches. If the session is
 * stale the action is hard-blocked and the MFA re-auth modal surfaces; on
 * success the operator's in-progress action resumes (its component never
 * unmounts, so its form state is preserved by construction). The server-side
 * gateMutation 401 stale_auth remains the authoritative backstop — withReauth()
 * also catches that 401, re-auths, and retries once.
 *
 * This consolidates the four previously-divergent reactive stale_auth handlers
 * (SupportPasswordModal, UserManagementPage, StaffActionZone, ActionConfirmModal)
 * into one path, removing the inconsistent "Refresh the page (Cmd+Shift+R)"
 * guidance.
 */

export const INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 1 hour
export const ABSOLUTE_CEILING_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Thrown by withReauth() when the operator cancels the re-auth modal. Callers
 *  catch this to abort silently (no error toast) — the action simply did not
 *  run because the session could not be made fresh. */
export class ReauthCancelledError extends Error {
  constructor() {
    super('reauth_cancelled');
    this.name = 'ReauthCancelledError';
  }
}

/** True for the admin-api fresh-auth 401 (and the API Gateway authoriser 401).
 *  Used by withReauth() to decide whether a failure is a staleness signal that
 *  should trigger re-auth + retry, vs. a genuine error to surface. */
export function isStaleAuthError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /\b401\b/.test(err.message) || err.message.includes('stale_auth');
}

interface RequireFreshOpts {
  /** Skip the proactive staleness check and force the modal open. Used on the
   *  reactive path when the server has already returned 401 stale_auth. */
  force?: boolean;
}

interface ReAuthContextValue {
  /** Proactive gate. Resolves true if the session is fresh (or becomes fresh
   *  via the modal), false if the operator cancels. */
  requireFreshAuth: (opts?: RequireFreshOpts) => Promise<boolean>;
  /** Wrap a mutating action: proactively gate, run, and on a stale_auth 401
   *  re-auth + retry once. Throws ReauthCancelledError if the operator cancels. */
  withReauth: <T>(action: () => Promise<T>) => Promise<T>;
}

const ReAuthContext = createContext<ReAuthContextValue | null>(null);

/** Read the Cognito `auth_time` (seconds since epoch) from the current session,
 *  or null if no session / no claim. Does not force a refresh — auth_time does
 *  not advance on refresh, so the cached value is the authoritative ceiling. */
async function readAuthTimeMs(): Promise<number | null> {
  try {
    const session = await fetchAuthSession();
    const authTime = session.tokens?.idToken?.payload?.auth_time;
    return typeof authTime === 'number' ? authTime * 1000 : null;
  } catch {
    return null;
  }
}

/** Best-effort read of the signed-in operator's email for the re-auth form. */
async function readEmail(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const email = session.tokens?.idToken?.payload?.email;
    return typeof email === 'string' ? email : null;
  } catch {
    return null;
  }
}

export function ReAuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const lastActivityRef = useRef<number>(Date.now());
  const resolversRef = useRef<Array<(ok: boolean) => void>>([]);
  const didSignOutRef = useRef<boolean>(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState<string>('');

  // Track last user interaction to drive the 1h inactivity bound. Writing a
  // ref (not state) keeps this allocation-free on every event — no re-render.
  useEffect(() => {
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const events: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'click',
      'scroll',
      'touchstart',
    ];
    for (const e of events) window.addEventListener(e, onActivity, { passive: true });
    return () => {
      for (const e of events) window.removeEventListener(e, onActivity);
    };
  }, []);

  const resolveAll = useCallback((ok: boolean) => {
    const resolvers = resolversRef.current;
    resolversRef.current = [];
    for (const r of resolvers) r(ok);
  }, []);

  /** Is the session stale right now? Stale = inactivity bound lapsed, OR the
   *  24h absolute ceiling reached, OR no provable session. */
  const isStale = useCallback(async (): Promise<boolean> => {
    const now = Date.now();
    if (now - lastActivityRef.current >= INACTIVITY_LIMIT_MS) return true;
    const authTimeMs = await readAuthTimeMs();
    if (authTimeMs === null) return true; // cannot prove freshness → treat as stale
    if (now - authTimeMs >= ABSOLUTE_CEILING_MS) return true;
    return false;
  }, []);

  const requireFreshAuth = useCallback(
    async (opts?: RequireFreshOpts): Promise<boolean> => {
      if (!opts?.force) {
        const stale = await isStale();
        if (!stale) return true;
      }
      // Stale (or forced): surface the modal and wait for the operator.
      setEmail((await readEmail()) ?? '');
      return new Promise<boolean>((resolve) => {
        resolversRef.current.push(resolve);
        setModalOpen(true);
      });
    },
    [isStale],
  );

  const withReauth = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T> => {
      const ok = await requireFreshAuth();
      if (!ok) throw new ReauthCancelledError();
      try {
        return await action();
      } catch (err) {
        if (isStaleAuthError(err)) {
          // Server backstop fired despite the proactive check (clock skew, a
          // ceiling crossed mid-request). Re-auth and retry exactly once.
          const ok2 = await requireFreshAuth({ force: true });
          if (!ok2) throw new ReauthCancelledError();
          return await action();
        }
        throw err;
      }
    },
    [requireFreshAuth],
  );

  /** The MFA step-up itself: clear the stale session, then run a fresh sign-in
   *  (password → TOTP) so Cognito issues tokens with an advanced auth_time. */
  const performReauth = useCallback(
    async (password: string, totpCode: string): Promise<void> => {
      setReauthInProgress(true);
      didSignOutRef.current = true;
      // signIn refuses to run while a user is authenticated; clear first. The
      // transient signedOut is suppressed in useAuth (see reauth-state.ts).
      try {
        await signOut();
      } catch {
        // No active session to clear — proceed to sign-in.
      }
      let step = await signIn({ username: email, password });
      if (!step.isSignedIn && step.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        step = await confirmSignIn({ challengeResponse: totpCode });
      }
      if (!step.isSignedIn) {
        // An enrolled staff member should land on the TOTP challenge; anything
        // else (new-password / TOTP-setup) means this account is not in a state
        // we can step up in place.
        throw new Error(`reauth_incomplete:${step.nextStep.signInStep}`);
      }
      // Fresh auth event landed — reset the inactivity bound and release the
      // suppression so the signedIn event re-syncs useAuth normally.
      lastActivityRef.current = Date.now();
      didSignOutRef.current = false;
      setReauthInProgress(false);
    },
    [email],
  );

  const onReauthSuccess = useCallback(() => {
    setModalOpen(false);
    resolveAll(true);
  }, [resolveAll]);

  const onReauthCancel = useCallback(() => {
    setModalOpen(false);
    setReauthInProgress(false);
    resolveAll(false);
    // If a sign-out already happened (the operator tried and gave up), the
    // session is gone — send them to sign-in. (No in-progress work is
    // preserved on the give-up path; the success path never reaches here.)
    if (didSignOutRef.current) {
      didSignOutRef.current = false;
      navigate('/signin', { replace: true });
    }
  }, [navigate, resolveAll]);

  const value = useMemo<ReAuthContextValue>(
    () => ({ requireFreshAuth, withReauth }),
    [requireFreshAuth, withReauth],
  );

  return (
    <ReAuthContext.Provider value={value}>
      {children}
      {modalOpen && (
        <ReAuthModal
          email={email}
          onPerform={performReauth}
          onSuccess={onReauthSuccess}
          onCancel={onReauthCancel}
        />
      )}
    </ReAuthContext.Provider>
  );
}

export function useReAuth(): ReAuthContextValue {
  const ctx = useContext(ReAuthContext);
  if (!ctx) {
    throw new Error('useReAuth must be used within a <ReAuthProvider>');
  }
  return ctx;
}

/** Map a re-auth failure to an operator-friendly message — never the raw
 *  Cognito/Amplify string (CLAUDE.md error-surfacing standard). */
function friendlyReauthError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const lower = raw.toLowerCase();
  if (lower.includes('incorrect') || lower.includes('notauthorized')) {
    return 'That password was not correct. Try again.';
  }
  if (lower.includes('code') && (lower.includes('mismatch') || lower.includes('invalid'))) {
    return 'That authenticator code was not accepted. Enter the current 6-digit code and try again.';
  }
  if (lower.includes('reauth_incomplete')) {
    return 'This account cannot be re-authenticated here. Sign out and sign in again to continue.';
  }
  if (lower.includes('limit') || lower.includes('attempts')) {
    return 'Too many attempts. Wait a moment, then try again.';
  }
  return 'Re-authentication failed. Check your password and authenticator code, then try again.';
}

function ReAuthModal({
  email,
  onPerform,
  onSuccess,
  onCancel,
}: {
  email: string;
  onPerform: (password: string, totpCode: string) => Promise<void>;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onPerform(password, totpCode);
      onSuccess();
    } catch (err) {
      // Raw error to console only — operator sees the friendly message.
      console.error('reauth: step-up failed', err);
      setError(friendlyReauthError(err));
      setTotpCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="portal-modal-backdrop" role="presentation">
      <div
        className="portal-modal portal-action-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reauth-title"
      >
        {/* PL10 (iter-2) — match the canonical ActionConfirmModal chrome exactly:
            top-right × close (was missing) so the dialog dismisses like every
            other staff modal. Cancel/close route through the same onCancel. */}
        <button
          type="button"
          className="portal-modal-close"
          onClick={onCancel}
          disabled={busy}
          aria-label="Close dialog"
        >
          ×
        </button>
        <h2 id="reauth-title">Confirm it&apos;s you</h2>
        <div className="portal-action-modal-body">
          <p>
            For your security, sign in again to continue. Your work on this page
            is kept — you&apos;ll return to exactly where you were.
          </p>
          {/* PL10 — the fields use the standard staff form chrome (labelled
              blocks + .portal-input), not the previous unstyled inline inputs.
              No auth logic changes: same fields, same submit/cancel behaviour. */}
          <form onSubmit={(e) => void submit(e)} aria-busy={busy} className="portal-reauth-form">
            <label className="portal-uat-field-label">
              <span>Email</span>
              <input className="portal-input" type="email" autoComplete="username" value={email} readOnly />
            </label>
            <label className="portal-uat-field-label">
              <span>Password</span>
              <input
                className="portal-input"
                type="password"
                autoComplete="current-password"
                required
                /* Focus moves into a modal dialog (role="dialog"
                   aria-modal="true") the user just triggered — not on page
                   load. Email above is readOnly, so Password is the first
                   editable field. WAI-ARIA dialog pattern, not drive-by
                   autofocus. */
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                disabled={busy}
              />
            </label>
            <label className="portal-uat-field-label">
              <span>6-digit authenticator code</span>
              <input
                className="portal-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                value={totpCode}
                onChange={(ev) => setTotpCode(ev.target.value)}
                disabled={busy}
              />
            </label>
            {error && (
              <div className="portal-error" role="alert">
                {error}
              </div>
            )}
            <div className="portal-modal-actions">
              <button
                type="button"
                className="portal-button-secondary"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </button>
              <button type="submit" className="portal-button-primary" disabled={busy}>
                {busy ? 'Verifying…' : 'Re-authenticate'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
